import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import {
  administrationPath,
  loggedOutPath,
  masterdataPath,
  myCampaignPath,
  myContactsPath,
  myContractsPath,
  myFieldsYieldsPath,
  myPaymentsPath,
  orderedDeliveredProductsPath as myOrdersPath,
  rootPath,
  userSettingsPath,
} from '../../../routes';
import { useAuthStore, useContextStore } from '../../../stores/storeRoot';
import {
  IconContainer,
  LogoutButton,
  MenuItem,
  MobileMenuContainer,
  UserProfile,
} from './styles';
import HomeIcon from '../../../assets/icons/sidebarIcons/StartIcon';
import MyContractsIcon from '../../../assets/icons/sidebarIcons/MyContractsIcon';
import MyOrdersIcon from '../../../assets/icons/sidebarIcons/MyOrdersIcon';
import MyCampaignIcon from '../../../assets/icons/sidebarIcons/MyCampaignIcon';
import MyPaymentsIcon from '../../../assets/icons/sidebarIcons/MyPaymentsIcon';
import MyFieldsYieldsIcon from '../../../assets/icons/sidebarIcons/MyFieldsYieldsIcon';
import MasterdataIcon from '../../../assets/icons/sidebarIcons/MasterdataIcon';
import UserIcon from '../../../assets/icons/sidebarIcons/UserIcon';
import LogoutIcon from '../../../assets/icons/sidebarIcons/LogoutIcon';
import MobileHeader from '../MobileHeader/MobileHeader';
import { SelectedNewsContext } from '../../../context/SelectedNewsContext';
import SettingsIcon from '../../../assets/icons/SettingsIcon';
import SecurityTrim from '../../../services/auth/SecurityTrim';
import { CropConnectRoleDefinitions } from '../../../services/cropConnectApiClient';
import AgriPartnerIcon from '../../../assets/icons/sidebarIcons/AgriPartnerIcon';
import { sideBarAdministrationPathSecurityTrimProps } from '../../../services/auth/predefinedPermissions/predefinedSecurityTrimProps';
import { myFieldsAndYieldsRequiredRoleDefinitions } from '../../../services/auth/predefinedPermissions/predefinedRoleDefinitionsGroups';

const MobileMenu: React.FC<{
  isMenuOpen: boolean;
  onClose: () => void;
  onMenuClick: () => void;
}> = ({ isMenuOpen, onClose, onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, activeBpNumber } = useContextStore();
  const { logout } = useAuthStore();
  const { setSelectedArticle } = useContext(SelectedNewsContext);

  // TODO Gosia: This was a quick fix for an issue with mobile menu not working properly.
  // It needs a better solution. Investigate and refactor to remove the setTimeout if possible.

  // setTimeout is used as a buffer to ensure the navigate action completes
  // (including passing the state) before onClose is called, which might otherwise
  // reset the DOM or state prematurely, causing the navigation to fail.

  const handleItemClick = (path: string, reset: boolean = false) => {
    if (reset) {
      setSelectedArticle(null);
      setTimeout(() => {
        navigate(path, { state: { reset: true } });
      }, 100);
    } else {
      setTimeout(() => {
        navigate(path);
      }, 100);
    }
    onClose();
  };

  const handleHomePageClick = () => {
    handleItemClick(rootPath, true);
  };

  const handleAgriPartnerRedirect = () => {
    const agriPartnerUrl = process.env.REACT_APP_SSO_INTEGRATION_PL;
    if (agriPartnerUrl) {
      window.open(agriPartnerUrl, '_blank');
    }
    onClose();
  };

  return (
    <>
      <MobileHeader onMenuClick={onMenuClick} isMenuOpen={isMenuOpen} />
      {isMenuOpen && (
        <MobileMenuContainer>
          <div>
            <MenuItem
              to={rootPath}
              onClick={handleHomePageClick}
              active={location.pathname === rootPath}
            >
              <IconContainer active={location.pathname === rootPath}>
                <HomeIcon
                  strokeColor={
                    location.pathname === rootPath ? 'white' : 'currentColor'
                  }
                />
              </IconContainer>
              {t(TranslationKeys.sidebar_home)}
            </MenuItem>
            {activeBpNumber && (
              <>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={myContractsPath}
                    active={location.pathname === myContractsPath}
                    onClick={() => handleItemClick(myContractsPath)}
                  >
                    <IconContainer
                      active={location.pathname === myContractsPath}
                    >
                      <MyContractsIcon
                        strokeColor={
                          location.pathname === myContractsPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_myContracts)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={myOrdersPath}
                    active={location.pathname === myOrdersPath}
                    onClick={() => handleItemClick(myOrdersPath)}
                  >
                    <IconContainer active={location.pathname === myOrdersPath}>
                      <MyOrdersIcon
                        strokeColor={
                          location.pathname === myOrdersPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_myOrders)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={myCampaignPath}
                    active={location.pathname === myCampaignPath}
                    onClick={() => handleItemClick(myCampaignPath)}
                  >
                    <IconContainer
                      active={location.pathname === myCampaignPath}
                    >
                      <MyCampaignIcon
                        strokeColor={
                          location.pathname === myCampaignPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_myCampaign)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={myPaymentsPath}
                    active={location.pathname === myPaymentsPath}
                    onClick={() => handleItemClick(myPaymentsPath)}
                  >
                    <IconContainer
                      active={location.pathname === myPaymentsPath}
                    >
                      <MyPaymentsIcon
                        strokeColor={
                          location.pathname === myPaymentsPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_myPayments)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={
                    myFieldsAndYieldsRequiredRoleDefinitions
                  }
                >
                  <MenuItem
                    to={myFieldsYieldsPath}
                    active={location.pathname === myFieldsYieldsPath}
                    onClick={() => handleItemClick(myFieldsYieldsPath)}
                  >
                    <IconContainer
                      active={location.pathname === myFieldsYieldsPath}
                    >
                      <MyFieldsYieldsIcon
                        strokeColor={
                          location.pathname === myFieldsYieldsPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_myFieldsYields)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={myContactsPath}
                    active={location.pathname === myContactsPath}
                    onClick={() => handleItemClick(myContactsPath)}
                  >
                    <IconContainer
                      active={location.pathname === myContactsPath}
                    >
                      <MasterdataIcon
                        strokeColor={
                          location.pathname === myContactsPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_contacts)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItem
                    to={masterdataPath}
                    active={location.pathname === masterdataPath}
                    onClick={() => handleItemClick(masterdataPath)}
                  >
                    <IconContainer
                      active={location.pathname === masterdataPath}
                    >
                      <MasterdataIcon
                        strokeColor={
                          location.pathname === masterdataPath
                            ? 'white'
                            : 'currentColor'
                        }
                      />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_masterdata)}
                  </MenuItem>
                </SecurityTrim>

                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower_PL,
                  ]}
                >
                  <MenuItem
                    onClick={handleAgriPartnerRedirect}
                    active={false}
                    to={''}
                  >
                    <IconContainer>
                      <AgriPartnerIcon />
                    </IconContainer>
                    {t(TranslationKeys.sidebar_agripartner_link)}
                  </MenuItem>
                </SecurityTrim>
              </>
            )}
          </div>
          <div>
            <SecurityTrim {...sideBarAdministrationPathSecurityTrimProps}>
              <UserProfile
                to={administrationPath}
                onClick={() => handleItemClick(administrationPath)}
              >
                <IconContainer>
                  <SettingsIcon isSidebarIcon />
                </IconContainer>
                {t(TranslationKeys.administrationPage_title)}
              </UserProfile>
            </SecurityTrim>

            <UserProfile
              to={userSettingsPath}
              onClick={() => handleItemClick(userSettingsPath)}
            >
              <IconContainer>
                <UserIcon />
              </IconContainer>
              {user?.displayName || 'User'}
            </UserProfile>
            <LogoutButton
              onClick={() => {
                handleItemClick(loggedOutPath);
                logout();
              }}
            >
              <IconContainer>
                <LogoutIcon />
              </IconContainer>
              {t(TranslationKeys.usermenu_item_logout)}
            </LogoutButton>
          </div>
        </MobileMenuContainer>
      )}
    </>
  );
};

export default MobileMenu;
